import { useState } from 'react';
import { ImageRejectedError, prepareImage } from '@/lib/image-upload';
import { trpc } from '@/lib/trpc';

/**
 * The three-step upload, as one call.
 *
 * `createUploadUrl` signs a PUT, the browser sends the bytes straight to R2, and `confirmUpload`
 * turns the object into a row. The API server never sees the file — which is the whole point of
 * the dance, and the reason the middle step is a bare `fetch` to another origin rather than
 * anything tRPC does.
 *
 * The phase is exposed rather than a percentage: a presigned PUT through `fetch` reports no
 * progress events, and a fake bar would be a lie about a request that can still fail at the end.
 */
export type UploadPhase = 'idle' | 'preparing' | 'uploading' | 'saving';

const PHASE_LABELS: Record<Exclude<UploadPhase, 'idle'>, string> = {
  preparing: 'Preparing image…',
  uploading: 'Uploading…',
  saving: 'Saving…',
};

export function uploadPhaseLabel(phase: UploadPhase): string | null {
  return phase === 'idle' ? null : PHASE_LABELS[phase];
}

export interface ImageUploader {
  upload: (file: File) => Promise<void>;
  phase: UploadPhase;
  isUploading: boolean;
  error: string | null;
  clearError: () => void;
}

export function useImageUpload(kind: 'avatar' | 'skin'): ImageUploader {
  const utils = trpc.useUtils();
  const createUploadUrl = trpc.uploads.createUploadUrl.useMutation();
  const confirmUpload = trpc.uploads.confirmUpload.useMutation();

  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File) => {
    if (phase !== 'idle') return;
    setError(null);
    setPhase('preparing');

    try {
      const prepared = await prepareImage(file);

      const signed = await createUploadUrl.mutateAsync({
        kind,
        contentType: prepared.contentType,
        byteSize: prepared.blob.size,
      });

      setPhase('uploading');
      const response = await fetch(signed.url, {
        method: 'PUT',
        // Signed into the URL, so anything else is refused by the bucket rather than stored.
        headers: { 'content-type': signed.contentType },
        body: prepared.blob,
      });
      if (!response.ok) {
        throw new Error(`The storage bucket refused the upload (${response.status}).`);
      }

      setPhase('saving');
      await confirmUpload.mutateAsync({
        key: signed.key,
        kind,
        width: prepared.width,
        height: prepared.height,
      });

      // An avatar is mirrored into `users.avatar_url`, so the profile read is stale too.
      await Promise.all([utils.uploads.listImages.invalidate(), utils.profile.me.invalidate()]);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPhase('idle');
    }
  };

  return {
    upload,
    phase,
    isUploading: phase !== 'idle',
    error,
    clearError: () => setError(null),
  };
}

/**
 * A refusal we wrote, a tRPC error the server wrote, or a network failure nobody wrote. Only the
 * last needs inventing a sentence for — the other two already say something useful.
 */
function messageFor(err: unknown): string {
  if (err instanceof ImageRejectedError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return 'The upload failed. Check your connection and try again.';
}
